/**
 * Phase 0.5 — Create-scope-node dialog.
 *
 * A grant is scoped to a scope_node, but until now the grant dialog took a
 * scope_node_id by manual entry with no way to mint one from the UI. This
 * dialog wires AdminCreateScopeNode: register the node that owns an object
 * (tenant comes from the JWT), then surface the new id so it can be pasted
 * into a grant. The scope-tree picker the grant dialog mentions is the
 * fuller follow-up; this closes the "where does a scope_node_id come from"
 * gap so the admin surface is self-contained.
 */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Input, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { useCreateScopeNode } from './hooks';
// Phase 3c: a scope node tagged with a legal sensitivity makes everything under
// it legal-restricted (NoView for non-attorneys + AuditRequired). "standard" is
// the default, non-restricted behavior.
const SENSITIVITIES = [
    'standard',
    'legal_privileged',
    'source_selection_sensitive',
    'protective_order'
];
export function CreateScopeNodeDialog({ open, onOpenChange }) {
    const [kind, setKind] = useState('project');
    const [targetObjectType, setTargetObjectType] = useState('');
    const [targetObjectId, setTargetObjectId] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [sensitivity, setSensitivity] = useState('standard');
    const [createdId, setCreatedId] = useState('');
    const create = useCreateScopeNode();
    const reset = ()=>{
        setTargetObjectType('');
        setTargetObjectId('');
        setDisplayName('');
        setSensitivity('standard');
    };
    const submit = async ()=>{
        if (!kind || !targetObjectType || !targetObjectId) return;
        const node = await create.mutateAsync({
            kind,
            targetObjectType,
            targetObjectId,
            displayName,
            sensitivity
        });
        if (node?.id) setCreatedId(node.id);
        reset();
    };
    const canSubmit = !!kind && !!targetObjectType && !!targetObjectId && !create.isPending;
    return /*#__PURE__*/ _jsx(Dialog, {
        open: open,
        onOpenChange: onOpenChange,
        children: /*#__PURE__*/ _jsxs(DialogContent, {
            "data-testid": Testids.adminAccess.scopeNodeDialog,
            children: [
                /*#__PURE__*/ _jsxs(DialogHeader, {
                    children: [
                        /*#__PURE__*/ _jsx(DialogTitle, {
                            children: "Create scope node"
                        }),
                        /*#__PURE__*/ _jsx(DialogDescription, {
                            children: "Registers the scope node that owns an object so grants can target it. The tenant is taken from your session. Copy the resulting id into a grant."
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "space-y-4 py-2",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-1",
                            children: [
                                /*#__PURE__*/ _jsx(Label, {
                                    htmlFor: "scope-kind",
                                    children: "Kind"
                                }),
                                /*#__PURE__*/ _jsx(Input, {
                                    id: "scope-kind",
                                    "data-testid": Testids.adminAccess.scopeNodeKindInput,
                                    value: kind,
                                    onChange: (e)=>setKind(e.target.value),
                                    placeholder: "project | program | contract | …"
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-1",
                            children: [
                                /*#__PURE__*/ _jsx(Label, {
                                    htmlFor: "scope-target-type",
                                    children: "Target object type"
                                }),
                                /*#__PURE__*/ _jsx(Input, {
                                    id: "scope-target-type",
                                    "data-testid": Testids.adminAccess.scopeNodeTargetTypeInput,
                                    value: targetObjectType,
                                    onChange: (e)=>setTargetObjectType(e.target.value),
                                    placeholder: "e.g. delivery.programs"
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-1",
                            children: [
                                /*#__PURE__*/ _jsx(Label, {
                                    htmlFor: "scope-target-id",
                                    children: "Target object ID"
                                }),
                                /*#__PURE__*/ _jsx(Input, {
                                    id: "scope-target-id",
                                    "data-testid": Testids.adminAccess.scopeNodeTargetIdInput,
                                    value: targetObjectId,
                                    onChange: (e)=>setTargetObjectId(e.target.value),
                                    placeholder: "the object's id (e.g. a project / program id)"
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-1",
                            children: [
                                /*#__PURE__*/ _jsx(Label, {
                                    htmlFor: "scope-display-name",
                                    children: "Display name (optional)"
                                }),
                                /*#__PURE__*/ _jsx(Input, {
                                    id: "scope-display-name",
                                    "data-testid": Testids.adminAccess.scopeNodeDisplayNameInput,
                                    value: displayName,
                                    onChange: (e)=>setDisplayName(e.target.value),
                                    placeholder: "Human-friendly label"
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-1",
                            children: [
                                /*#__PURE__*/ _jsx(Label, {
                                    htmlFor: "scope-sensitivity",
                                    children: "Sensitivity"
                                }),
                                /*#__PURE__*/ _jsxs(Select, {
                                    value: sensitivity,
                                    onValueChange: setSensitivity,
                                    children: [
                                        /*#__PURE__*/ _jsx(SelectTrigger, {
                                            id: "scope-sensitivity",
                                            "data-testid": Testids.adminAccess.scopeNodeSensitivitySelect,
                                            children: /*#__PURE__*/ _jsx(SelectValue, {})
                                        }),
                                        /*#__PURE__*/ _jsx(SelectContent, {
                                            children: SENSITIVITIES.map((s)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                    value: s,
                                                    children: s
                                                }, s))
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("p", {
                                    className: "text-muted-foreground text-xs",
                                    children: [
                                        "Legal sensitivities (everything but ",
                                        /*#__PURE__*/ _jsx("code", {
                                            children: "standard"
                                        }),
                                        ") make content under this node NoView for non-attorneys and audit every access."
                                    ]
                                })
                            ]
                        }),
                        createdId && /*#__PURE__*/ _jsxs("p", {
                            className: "text-sm",
                            "data-testid": Testids.adminAccess.scopeNodeResult,
                            children: [
                                "Created scope node:",
                                ' ',
                                /*#__PURE__*/ _jsx("code", {
                                    className: "bg-muted rounded px-1 py-0.5 text-xs",
                                    children: createdId
                                })
                            ]
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs(DialogFooter, {
                    children: [
                        /*#__PURE__*/ _jsx(Button, {
                            variant: "outline",
                            onClick: ()=>onOpenChange(false),
                            children: "Close"
                        }),
                        /*#__PURE__*/ _jsx(Button, {
                            "data-testid": Testids.adminAccess.scopeNodeSubmitButton,
                            onClick: submit,
                            disabled: !canSubmit,
                            children: create.isPending ? 'Creating…' : 'Create scope node'
                        })
                    ]
                })
            ]
        })
    });
}
